import { Vote } from "lucide-react";
import { DiscordChannel, DiscordRole, GuildConfig } from "@/lib/api";
import { Section, Field } from "@/components/panel/Section";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ChannelPicker } from "@/components/panel/ChannelPicker";
import { MultiSelect } from "@/components/MultiSelect";
import { Switch } from "@/components/ui/switch";

type PollsConfig = GuildConfig["polls"];

type Props = {
  config: GuildConfig;
  channels: DiscordChannel[];
  roles: DiscordRole[];
  onChange: (v: PollsConfig) => void;
};

export function PollsTab({ config, channels, roles, onChange }: Props) {
  const cfg = config.polls ?? {
    enabled: false,
    allowed_channel_ids: [],
    manager_role_ids: [],
    default_duration_hours: 24,
    max_options: 5,
    allow_multiple_default: false,
    anonymous_default: true,
    show_live_results: true,
    show_poll_duration: true,
    embed_color: "#5865F2",
    log_channel_id: "",
  };
  const textChannels = channels.filter((ch) => ch.type === 0);
  const channelOptions = textChannels.map((ch) => ({ id: ch.id, name: ch.name }));
  const roleOptions = roles.map((r) => ({ id: r.id, name: r.name, color: r.color }));

  const update = <K extends keyof PollsConfig>(key: K, value: PollsConfig[K]) => {
    onChange({ ...cfg, [key]: value });
  };

  return (
    <Section
      icon={Vote}
      title="Polls"
      description="Create clean embed polls with Discord reaction voting, channel restrictions, manager roles and custom colors."
      enabled={cfg.enabled}
      onToggle={(v) => update("enabled", v)}
    >
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground space-y-2">
        <p className="font-medium text-foreground">How it works</p>
        <p>
          Members type <code className="text-primary">/poll</code> in any text channel, fill in a question and 2–10
          comma-separated options, and the bot posts a numbered poll (1️⃣ 2️⃣ 3️⃣ …) and reacts with the matching number
          emoji so people can vote by clicking them. If <strong>Poll manager roles</strong> below is set, only those
          roles (or Administrators) can use the command.
        </p>
      </div>

      <Card className="p-4 bg-muted/30">
        <h4 className="font-semibold mb-2 text-sm">Slash command</h4>
        <div className="space-y-3 text-sm">
          <div className="flex flex-col gap-1">
            <code className="text-primary font-mono text-xs">/poll question:&lt;text&gt; options:&lt;text&gt;</code>
            <span className="text-xs text-muted-foreground">
              <code>question</code> — what you're asking. <code>options</code> — <strong>comma-separated</strong>,
              e.g. <code>Red, Blue, Green</code> (2 to 10 options, both required).
            </span>
          </div>
          <div className="rounded border border-border/60 bg-background/40 p-2 text-xs text-muted-foreground">
            Example: <code className="text-primary">/poll question: Which map tonight? options: Nuketown, Dust2, Mirage</code>
            <br />
            Posts in the channel the command was used in — or in <strong>Log channel</strong> below, if you set one.
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Allowed poll channels" hint="Empty = polls can be created in every text channel.">
          <MultiSelect
            options={channelOptions}
            value={cfg.allowed_channel_ids}
            onChange={(ids) => update("allowed_channel_ids", ids)}
            prefix="#"
            placeholder="+ channel"
          />
        </Field>
        <Field label="Poll manager roles" hint="Empty = members with Manage Server/Admin can create polls.">
          <MultiSelect
            options={roleOptions}
            value={cfg.manager_role_ids}
            onChange={(ids) => update("manager_role_ids", ids)}
            prefix="@"
            placeholder="+ role"
          />
        </Field>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <Field
          label="Default duration (hours)"
          hint="Used when /poll omits duration. You can hide it from the embed below."
        >
          <Input
            type="number"
            min={1}
            max={720}
            value={cfg.default_duration_hours}
            onChange={(e) => update("default_duration_hours", Math.max(1, Number(e.target.value) || 24))}
          />
        </Field>
        <Field label="Max options">
          <Input
            type="number"
            min={2}
            max={10}
            value={cfg.max_options}
            onChange={(e) => update("max_options", Math.max(2, Math.min(10, Number(e.target.value) || 5)))}
          />
        </Field>
        <Field label="Embed color">
          <Input
            type="color"
            value={cfg.embed_color || "#5865F2"}
            onChange={(e) => update("embed_color", e.target.value)}
          />
        </Field>
        <Field label="Log channel" hint="Optional. If set, polls are posted here instead of the channel /poll was used in.">
          <ChannelPicker
            channels={textChannels}
            value={cfg.log_channel_id}
            onChange={(v) => update("log_channel_id", v === "__none__" ? "" : v)}
          />
        </Field>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-border p-4">
        <div className="space-y-0.5 min-w-0">
          <div className="text-sm font-medium">Show closing time on poll</div>
          <p className="text-xs text-muted-foreground leading-snug">
            Turn off to hide “closes in …” from the embed footer. Duration above still applies as the default when you
            omit <code className="text-primary">duration_hours</code> on <code className="text-primary">/poll</code>.
          </p>
        </div>
        <Switch
          checked={cfg.show_poll_duration !== false}
          onCheckedChange={(v) => update("show_poll_duration", v)}
          className="shrink-0"
        />
      </div>

      <div className="rounded-lg border border-border bg-muted/20 p-4 text-xs text-muted-foreground">
        Reaction polls use Discord's native reaction counts, so votes are visible directly under
        the embed and members vote without extra buttons.
      </div>
    </Section>
  );
}
